import { useState } from 'react';
import uniqid from 'uniqid';
import classNames from 'classnames';

import Icons from 'assets/icons';

import Report from 'containers/user/reports/report-section/report-tile/report-tile';
import Heading from 'components/partials/heading/heading';

import {
  getProductTypeDescription,
  type ProductTypeDictionary,
} from 'containers/user/reports/reports-utils';
import type { ReportTileData } from 'containers/user/reports/report-section/report-tile/report-tile';

interface ReportSectionProps {
  productType: string;
  reports: ReportTileData[];
  dictionary: ProductTypeDictionary;
  navigateToReport: (reportId: string | number) => void;
  isDefaultOpen: boolean;
}

const ReportSection = ({
  productType,
  reports,
  dictionary,
  navigateToReport,
  isDefaultOpen,
}: ReportSectionProps): React.JSX.Element => {
  const [isOpen, setIsOpen] = useState<boolean>(isDefaultOpen);

  const getIconForProductType = (
    productTypeArg: string,
  ): React.JSX.Element | null => {
    const inconsStyles = 'text-navy-800 size-5';

    switch (productTypeArg) {
      case 'PROSPECT_VIEW':
        return <Icons.Funnel customClass={inconsStyles} />;
      case 'ADMISSIONS_VIEW':
        return <Icons.Learn customClass={inconsStyles} />;
      case 'FINANCIAL_AID_VIEW':
        return <Icons.Finance customClass={inconsStyles} />;
      case 'RETENTION_VIEW':
        return <Icons.People customClass={inconsStyles} />;
      default:
        return null;
    }
  };

  const toggleSection = (): void => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="my-5">
      <button
        className="flex items-center mb-4 transition-colors duration-200"
        onClick={toggleSection}
      >
        <div className="flex items-center">
          {getIconForProductType(productType)}
          <Heading customClass="!font-bold ml-2" variant="h3">
            {getProductTypeDescription(productType, dictionary) || productType}
          </Heading>
        </div>
        {!!reports.length && (
          <Icons.Arrow
            className={classNames(
              'text-navy-800 ml-2 transform transition-all duration-300 ease-in-out',
              { 'rotate-90': isOpen, 'rotate-0': !isOpen },
            )}
          />
        )}
      </button>

      <div
        className={classNames(
          'flex flex-wrap gap-4 overflow-hidden transition-all duration-300 ease-in-out items-stretch',
          {
            'max-h-[2000px] opacity-100 mt-4': isOpen,
            'max-h-0 opacity-0 mt-0': !isOpen,
          },
        )}
      >
        {reports.map((report) => (
          <div key={uniqid()} className="w-[328px] mb-5">
            <Report
              navigateToReport={() => navigateToReport(report.id)}
              report={report}
              isOpen={isOpen}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default ReportSection;
